import { Redirect, Route, Switch } from "react-router-dom";
import { Login } from "components/main/Login";
import { SelectMunicipality } from "components/main/SelectMunicipality";
import { URL } from "myconstants";
import { Help } from "components/main/Help";
import { useEffect, useReducer } from "react";
import { ZoningPlanLegal } from "components/main/ZoningPlanLegal";
import { ZoningPlanDraft } from "components/main/ZoningPlanDraft";
import { ReactElement } from "react";
import { Error } from "components/main/Error";
import { Action, State } from "state/state";
import { Header } from "components/main/Header";
import { Footer } from "components/main/Footer";
import { ParameterSetDto, ParameterSetNameDto } from "dtos/dtos";

const initialState: State = {
  metrics: undefined,
  parameters: undefined,
  municipalities: undefined,
  selectedMunicipality: undefined,
  zoningPlanLegal: {
    scenario: undefined,
    parameterSet: undefined,
    calculationState: undefined,
  },
  zoningPlanDraft: {
    scenarioNames: undefined,
    parameterSetNames: undefined,
    parameterSetNamesLeft: undefined,
    parameterSetNamesRight: undefined,
    selectedScenarioLeft: undefined,
    selectedScenarioRight: undefined,
    selectedParameterSetLeft: undefined,
    selectedParameterSetRight: undefined,
    calculationStateLeft: undefined,
    calculationStateRight: undefined,
    selectedScenario: undefined,
    selectedParameterSet: undefined,
  },
  user: { user: undefined, accessibleMunicipalities: undefined },
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "clearAll":
      return {
        metrics: undefined,
        parameters: undefined,
        municipalities: undefined,
        selectedMunicipality: undefined,
        zoningPlanLegal: {
          scenario: undefined,
          parameterSet: undefined,
          calculationState: undefined,
        },
        zoningPlanDraft: {
          scenarioNames: undefined,
          parameterSetNames: undefined,
          parameterSetNamesLeft: undefined,
          parameterSetNamesRight: undefined,
          selectedScenarioLeft: undefined,
          selectedScenarioRight: undefined,
          selectedParameterSetLeft: undefined,
          selectedParameterSetRight: undefined,
          calculationStateLeft: undefined,
          calculationStateRight: undefined,
          selectedScenario: undefined,
          selectedParameterSet: undefined,
        },
        user: state.user,
      };
    case "setMetadata":
      return {
        ...state,
        metrics: action.data.metrics,
        parameters: action.data.parameters,
        municipalities: action.data.municipalities,
        zoningPlanLegal: {
          ...state.zoningPlanLegal,
          calculationState: undefined,
        },
        zoningPlanDraft: {
          ...state.zoningPlanDraft,
          calculationStateLeft: undefined,
          calculationStateRight: undefined,
        },
      };

    case "setSelectedMunicipality":
      return {
        ...state,
        selectedMunicipality: action.data.municipality,
        zoningPlanLegal: {
          scenario: undefined,
          parameterSet: undefined,
          calculationState: undefined,
        },
        zoningPlanDraft: {
          scenarioNames: undefined,
          parameterSetNames: undefined,
          parameterSetNamesLeft: undefined,
          parameterSetNamesRight: undefined,
          selectedScenarioLeft: undefined,
          selectedScenarioRight: undefined,
          selectedParameterSetLeft: undefined,
          selectedParameterSetRight: undefined,
          calculationStateLeft: undefined,
          calculationStateRight: undefined,
          selectedScenario: undefined,
          selectedParameterSet: undefined,
        },
      };

    case "setCalculationStateLegal":
      return {
        ...state,
        zoningPlanLegal: {
          ...state.zoningPlanLegal,
          calculationState: action.data.calculationState,
        },
      };

    case "setScenarioNamesDraft":
      return {
        ...state,
        zoningPlanDraft: {
          ...state.zoningPlanDraft,
          scenarioNames: action.data.scenarioNames,
          parameterSetNames: undefined,
          parameterSetNamesLeft: undefined,
          parameterSetNamesRight: undefined,
          selectedScenarioLeft: undefined,
          selectedScenarioRight: undefined,
          selectedParameterSetLeft: undefined,
          selectedParameterSetRight: undefined,
          calculationStateLeft: undefined,
          calculationStateRight: undefined,
          selectedScenario: undefined,
          selectedParameterSet: undefined,
        },
      };

    case "setScenarioAndParameterSetLegal":
      return {
        ...state,
        zoningPlanLegal: {
          ...state.zoningPlanLegal,
          scenario: action.data.scenario,
          parameterSet: action.data.parameterSet,
          calculationState: undefined,
        },
      };

    case "setSelectedScenarioLeft":
      return {
        ...state,
        zoningPlanDraft: {
          ...state.zoningPlanDraft,
          selectedScenarioLeft: action.data.scenario,
          parameterSetNamesLeft: action.data.parameterSetNames,
          selectedParameterSetLeft: undefined,
          calculationStateLeft: undefined,
        },
      };

    case "setSelectedScenarioRight":
      return {
        ...state,
        zoningPlanDraft: {
          ...state.zoningPlanDraft,
          selectedScenarioRight: action.data.scenario,
          parameterSetNamesRight: action.data.parameterSetNames,
          selectedParameterSetRight: undefined,
          calculationStateRight: undefined,
        },
      };

    case "setSelectedParameterSetLeft":
      return {
        ...state,
        zoningPlanDraft: {
          ...state.zoningPlanDraft,
          selectedParameterSetLeft: action.data.parameterSet,
          calculationStateLeft: undefined,
        },
      };

    case "setSelectedParameterSetRight":
      return {
        ...state,
        zoningPlanDraft: {
          ...state.zoningPlanDraft,
          selectedParameterSetRight: action.data.parameterSet,
          calculationStateRight: undefined,
        },
      };

    case "setCalculationStateLeft":
      return {
        ...state,
        zoningPlanDraft: {
          ...state.zoningPlanDraft,
          calculationStateLeft: action.data.calculationState,
        },
      };

    case "setCalculationStateRight":
      return {
        ...state,
        zoningPlanDraft: {
          ...state.zoningPlanDraft,
          calculationStateRight: action.data.calculationState,
        },
      };

    case "setSelectedScenarioDraft":
      return {
        ...state,
        zoningPlanDraft: {
          ...state.zoningPlanDraft,
          selectedScenario: action.data.scenario,
          selectedParameterSet: undefined,
          parameterSetNames: action.data.parameterSetNames,
        },
      };

    case "setSelectedParameterSetDraft":
      return {
        ...state,
        zoningPlanDraft: {
          ...state.zoningPlanDraft,
          selectedParameterSet: action.data.parameterSet,
        },
      };

    case "saveParameterSet": {
      const parameterSet: ParameterSetDto = action.data.parameterSet;

      const updateLegal =
        state.zoningPlanLegal.parameterSet !== undefined &&
        state.zoningPlanLegal.parameterSet.id === parameterSet.id;

      const updateLeft =
        state.zoningPlanDraft.selectedParameterSetLeft !== undefined &&
        state.zoningPlanDraft.selectedParameterSetLeft.id === parameterSet.id;

      const updateRight =
        state.zoningPlanDraft.selectedParameterSetRight !== undefined &&
        state.zoningPlanDraft.selectedParameterSetRight.id === parameterSet.id;

      return {
        ...state,
        zoningPlanLegal: {
          ...state.zoningPlanLegal,
          parameterSet: updateLegal
            ? parameterSet
            : state.zoningPlanLegal.parameterSet,
          calculationState: updateLegal
            ? undefined
            : state.zoningPlanLegal.calculationState,
        },
        zoningPlanDraft: {
          ...state.zoningPlanDraft,
          selectedParameterSetLeft: updateLeft
            ? parameterSet
            : state.zoningPlanDraft.selectedParameterSetLeft,
          selectedParameterSetRight: updateRight
            ? parameterSet
            : state.zoningPlanDraft.selectedParameterSetRight,
          calculationStateLeft: updateLeft
            ? undefined
            : state.zoningPlanDraft.calculationStateLeft,
          calculationStateRight: updateRight
            ? undefined
            : state.zoningPlanDraft.calculationStateRight,
        },
      };
    }

    case "renameParameterSet": {
      const parameterSetId: number = action.data.parameterSetId;
      const newName: string = action.data.newName;
      const parameterSetName = state.zoningPlanDraft.parameterSetNames?.find(
        (ps) => ps.id === parameterSetId
      );
      const parameterSetNameLeft =
        state.zoningPlanDraft.parameterSetNamesLeft?.find(
          (ps) => ps.id === parameterSetId
        );
      const parameterSetNameRight =
        state.zoningPlanDraft.parameterSetNamesRight?.find(
          (ps) => ps.id === parameterSetId
        );
      const selectedParameterSet = state.zoningPlanDraft.selectedParameterSet;
      const selectedParameterSetLeft =
        state.zoningPlanDraft.selectedParameterSetLeft;
      const selectedParameterSetRight =
        state.zoningPlanDraft.selectedParameterSetRight;

      if (parameterSetName !== undefined) {
        parameterSetName.name = newName;
      }

      if (parameterSetNameLeft !== undefined) {
        parameterSetNameLeft.name = newName;
      }

      if (parameterSetNameRight !== undefined) {
        parameterSetNameRight.name = newName;
      }

      if (
        selectedParameterSet !== undefined &&
        selectedParameterSet.id === parameterSetId
      ) {
        selectedParameterSet.name = newName;
      }

      if (
        selectedParameterSetLeft !== undefined &&
        selectedParameterSetLeft.id === parameterSetId
      ) {
        selectedParameterSetLeft.name = newName;
      }

      if (
        selectedParameterSetRight !== undefined &&
        selectedParameterSetRight.id === parameterSetId
      ) {
        selectedParameterSetRight.name = newName;
      }

      return {
        ...state,
      };
    }

    case "deleteParameterSet": {
      const parameterSetId: number = action.data.parameterSetId;

      const updateLeft =
        state.zoningPlanDraft.selectedParameterSetLeft !== undefined &&
        state.zoningPlanDraft.selectedParameterSetLeft.id === parameterSetId;
      const updateRight =
        state.zoningPlanDraft.selectedParameterSetRight !== undefined &&
        state.zoningPlanDraft.selectedParameterSetRight.id === parameterSetId;

      return {
        ...state,
        zoningPlanDraft: {
          ...state.zoningPlanDraft,
          parameterSetNames:
            state.zoningPlanDraft.parameterSetNames !== undefined
              ? state.zoningPlanDraft.parameterSetNames.filter(
                  (ps) => ps.id !== parameterSetId
                )
              : undefined,
          parameterSetNamesLeft:
            state.zoningPlanDraft.parameterSetNamesLeft !== undefined
              ? state.zoningPlanDraft.parameterSetNamesLeft.filter(
                  (ps) => ps.id !== parameterSetId
                )
              : undefined,
          parameterSetNamesRight:
            state.zoningPlanDraft.parameterSetNamesRight !== undefined
              ? state.zoningPlanDraft.parameterSetNamesRight.filter(
                  (ps) => ps.id !== parameterSetId
                )
              : undefined,
          selectedParameterSet:
            state.zoningPlanDraft.selectedParameterSet !== undefined &&
            state.zoningPlanDraft.selectedParameterSet.id === parameterSetId
              ? undefined
              : state.zoningPlanDraft.selectedParameterSet,
          selectedParameterSetLeft: updateLeft
            ? undefined
            : state.zoningPlanDraft.selectedParameterSetLeft,
          selectedParameterSetRight: updateRight
            ? undefined
            : state.zoningPlanDraft.selectedParameterSetRight,
          calculationStateLeft: updateLeft
            ? undefined
            : state.zoningPlanDraft.calculationStateLeft,
          calculationStateRight: updateRight
            ? undefined
            : state.zoningPlanDraft.calculationStateRight,
        },
      };
    }

    case "addParameterSet":
      const addedParameterSet: ParameterSetDto = action.data.parameterSet;

      let newParameterSetNames: ParameterSetNameDto[] | undefined = undefined;
      if (
        state.zoningPlanDraft.selectedScenario !== undefined &&
        state.zoningPlanDraft.parameterSetNames !== undefined &&
        state.zoningPlanDraft.selectedScenario.id ===
          addedParameterSet.scenarioId
      ) {
        newParameterSetNames = [
          ...state.zoningPlanDraft.parameterSetNames,
          { id: addedParameterSet.id, name: addedParameterSet.name },
        ];
      } else {
        newParameterSetNames = state.zoningPlanDraft.parameterSetNames;
      }

      let newParameterSetNamesLeft: ParameterSetNameDto[] | undefined =
        undefined;
      if (
        state.zoningPlanDraft.selectedScenarioLeft !== undefined &&
        state.zoningPlanDraft.parameterSetNamesLeft !== undefined &&
        state.zoningPlanDraft.selectedScenarioLeft.id ===
          addedParameterSet.scenarioId
      ) {
        newParameterSetNamesLeft = [
          ...state.zoningPlanDraft.parameterSetNamesLeft,
          { id: addedParameterSet.id, name: addedParameterSet.name },
        ];
      } else {
        newParameterSetNamesLeft = state.zoningPlanDraft.parameterSetNamesLeft;
      }

      let newParameterSetNamesRight: ParameterSetNameDto[] | undefined =
        undefined;
      if (
        state.zoningPlanDraft.selectedScenarioRight !== undefined &&
        state.zoningPlanDraft.parameterSetNamesRight !== undefined &&
        state.zoningPlanDraft.selectedScenarioRight.id ===
          addedParameterSet.scenarioId
      ) {
        newParameterSetNamesRight = [
          ...state.zoningPlanDraft.parameterSetNamesRight,
          { id: addedParameterSet.id, name: addedParameterSet.name },
        ];
      } else {
        newParameterSetNamesRight =
          state.zoningPlanDraft.parameterSetNamesRight;
      }

      return {
        ...state,
        zoningPlanDraft: {
          ...state.zoningPlanDraft,
          selectedParameterSet: addedParameterSet,
          parameterSetNames: newParameterSetNames,
          parameterSetNamesLeft: newParameterSetNamesLeft,
          parameterSetNamesRight: newParameterSetNamesRight,
        },
      };

    case "signIn":
      return {
        ...state,
        user: {
          user: action.data.user,
          accessibleMunicipalities: undefined,
        },
      };

    case "setAccessibleMunicipalities":
      return {
        ...state,
        user: {
          ...state.user,
          accessibleMunicipalities: action.data.municipalities,
        },
      };

    case "signOut":
      return initialState;

    default:
      throw Error();
  }
}

function App(): ReactElement {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    async function authenticate() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/users/authenticate`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (response.ok) {
          const user = await response.json();
          dispatch({ type: "signIn", data: { user: user } });
        }
      } catch (error) {
        dispatch({ type: "signOut" });
      }
    }

    authenticate();
  }, []);

  return (
    <>
      <div className="flex flex-col h-full w-full 3xl:w-3/4 mx-auto justify-between font-arial">
        <Header dispatch={dispatch} state={state} />
        <Switch>
          <Route exact path={URL.root}>
            <Redirect to={URL.login} />
          </Route>
          <Route exact path={URL.login}>
            <Login dispatch={dispatch} state={state} />
          </Route>
          <Route exact path={URL.selectMunicipality}>
            <SelectMunicipality dispatch={dispatch} state={state} />
          </Route>
          <Route path={URL.zoningPlanLegal}>
            <ZoningPlanLegal dispatch={dispatch} state={state} />
          </Route>
          <Route path={URL.zoningPlanDraft}>
            <ZoningPlanDraft dispatch={dispatch} state={state} />
          </Route>
          <Route exact path={URL.help}>
            <Help />
          </Route>
          <Route exact path={URL.error}>
            <Error />
          </Route>
        </Switch>
        <Footer />
      </div>
    </>
  );
}

export default App;
